// React-side bridge for the Catawiki Browser Extension.
// The extension's content script posts `window.postMessage({source:'catawiki-bridge',...})`
// and listens for messages from `{source:'catawiki-bridge-app'}`.
//
// IMPORTANT: We attach state to `window` (not module scope) so that React Hot
// Module Replacement does not orphan the listener from the request map. With
// pure module-level state, HMR re-evaluates the module → new `_pendingRequests`
// Map → but the previously-registered listener still closes over the OLD map,
// so responses arrive with "no resolver for requestId" and state never updates.
import { useCallback, useEffect, useRef, useState } from "react";

const APP_TAG = "catawiki-bridge";
const REQ_TIMEOUT_MS = 180000; // 3 min — large batches with PerimeterX rate limits

function getBridgeState() {
  if (typeof window === "undefined") {
    return { pending: new Map(), counter: { n: 0 } };
  }
  if (!window.__catawikiBridge) {
    window.__catawikiBridge = { pending: new Map(), counter: { n: 0 } };
  }
  return window.__catawikiBridge;
}

function handleExtensionMessage(event) {
  if (event.source !== window) return;
  const msg = event.data;
  if (!msg || msg.source !== APP_TAG) return;
  const state = getBridgeState();

  // Streamed partial result — one lot at a time.
  if (msg.type === "SHIPPING_PARTIAL" && msg.requestId != null && msg.lotId != null) {
    const req = state.pending.get(msg.requestId);
    if (req?.onPartial) req.onPartial(msg.lotId, msg.data);
    return;
  }

  if (msg.type !== "SHIPPING_BATCH_RESULT" || msg.requestId == null) return;

  const batch = msg.data || {};
  const keys = Object.keys(batch);
  const sampleKey = keys[0];
  const sample = sampleKey ? batch[sampleKey] : null;
  const withShipping = keys.filter((k) => typeof batch[k]?.shipping_eur === "number").length;
  const withPickup = keys.filter((k) => batch[k]?.pickup_city).length;
  const withErrors = keys.filter((k) => batch[k]?.error).length;
  console.log(
    `[Catawiki App] batch DONE req=${msg.requestId} ${keys.length} lots ` +
      `(shipping=${withShipping}, pickup=${withPickup}, errors=${withErrors})`,
    sample ? { sampleId: sampleKey, sample } : "(empty)",
  );

  const req = state.pending.get(msg.requestId);
  if (req) {
    state.pending.delete(msg.requestId);
    req.resolve(msg);
  } else {
    console.warn("[Catawiki App] no resolver for requestId", msg.requestId);
  }
}

// Idempotent listener registration — survives module re-evaluation under HMR.
if (typeof window !== "undefined") {
  if (window.__catawikiBridgeListener) {
    window.removeEventListener("message", window.__catawikiBridgeListener);
  }
  window.__catawikiBridgeListener = handleExtensionMessage;
  window.addEventListener("message", handleExtensionMessage);
}

export function isExtensionInstalled() {
  return new Promise((resolve) => {
    let resolved = false;
    function onReady(event) {
      if (event.source !== window) return;
      if (event.data?.source !== APP_TAG || event.data?.type !== "READY") return;
      if (resolved) return;
      resolved = true;
      window.removeEventListener("message", onReady);
      resolve({ installed: true, version: event.data.version });
    }
    window.addEventListener("message", onReady);
    window.postMessage({ source: `${APP_TAG}-app`, type: "PING" }, "*");
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        window.removeEventListener("message", onReady);
        resolve({ installed: false });
      }
    }, 800);
  });
}

export function fetchShippingBatch(lotIds, onPartial) {
  if (!Array.isArray(lotIds) || lotIds.length === 0) return Promise.resolve({});
  const state = getBridgeState();
  const requestId = ++state.counter.n;
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      state.pending.delete(requestId);
      console.warn(`[Catawiki App] requestId ${requestId} timed out after ${REQ_TIMEOUT_MS}ms`);
      resolve({});
    }, REQ_TIMEOUT_MS);
    state.pending.set(requestId, {
      resolve: (msg) => {
        clearTimeout(timer);
        resolve(msg.ok ? msg.data || {} : {});
      },
      onPartial,
    });
    console.log(`[Catawiki App] sending FETCH_SHIPPING_BATCH req=${requestId} for ${lotIds.length} lots`);
    window.postMessage(
      { source: `${APP_TAG}-app`, type: "FETCH_SHIPPING_BATCH", lotIds, requestId },
      "*",
    );
  });
}

export function useExtensionStatus() {
  const [status, setStatus] = useState({ checked: false, installed: false });
  useEffect(() => {
    isExtensionInstalled().then((r) =>
      setStatus({ checked: true, installed: r.installed, version: r.version }),
    );
  }, []);
  return status;
}

export function useShippingEnrichment(items) {
  const [enrichedById, setEnrichedById] = useState({});
  const seenRef = useRef(new Set());

  useEffect(() => {
    if (!Array.isArray(items) || items.length === 0) return;
    const newIds = items
      .map((it) => it.id)
      .filter((id) => id != null && !seenRef.current.has(id));
    if (newIds.length === 0) return;
    newIds.forEach((id) => seenRef.current.add(id));
    const onPartial = (lotId, data) => {
      if (!data || data.error) return;
      setEnrichedById((prev) => ({ ...prev, [lotId]: data }));
    };
    fetchShippingBatch(newIds, onPartial).then((batch) => {
      const batchKeys = Object.keys(batch || {});
      if (batchKeys.length === 0) {
        // Either streaming filled state already, or the request failed.
        return;
      }
      setEnrichedById((prev) => {
        const next = { ...prev, ...batch };
        console.log(
          `[Catawiki App] enrichedById final: ${Object.keys(next).length} total entries`,
        );
        return next;
      });
    });
  }, [items]);

  const merge = useCallback(
    (item) => {
      const extra = enrichedById[item.id];
      if (!extra || extra.error) return item;
      return {
        ...item,
        seller_country: extra.seller_country ?? item.seller_country,
        seller_country_code: extra.seller_country_code ?? item.seller_country_code,
        pickup_city: extra.pickup_city ?? item.pickup_city,
        shipping_eur: extra.shipping_eur ?? item.shipping_eur,
        shipping_days_from: extra.shipping_days_from ?? item.shipping_days_from,
        shipping_days_to: extra.shipping_days_to ?? item.shipping_days_to,
        pickup_only: extra.pickup_only ?? item.pickup_only,
        shipping_unavailable: extra.shipping_unavailable || item.shipping_unavailable,
      };
    },
    [enrichedById],
  );

  return { merge };
}
